import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Injector,
  Input,
  OnChanges,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { CsvColumn } from 'src/app/models/csv-export.model';
import {
  PrivilegedProfile,
  allowOpenLogUILinks,
} from 'src/app/models/user.model';
import {
  DeviceDetailsDto,
  DeviceDto,
  PartDto,
  QsTrinityProDetailsDto,
  QsUavDetailsDto,
  SessionDto,
} from 'src/app/models/device.model';
import { CsvExportService } from 'src/app/services/csv-export.service';
import { DeviceService } from 'src/app/services/device.service';
import { environment } from 'src/environments/environment';
import {
  matchesDroneConfiguration,
  resolveSessionConfiguration,
} from 'src/app/utils/drone-configuration';
import {
  getDeviceDisplayName,
  getDeviceUnitNumber,
} from 'src/app/utils/device-display';
import { resolveLogUiHardwareRevision } from 'src/app/utils/logui';
import { buildTimestamp, formatTimestamp } from 'src/utils/general-utils';
import { formatPartSubType } from 'src/app/utils/part-display';
import { PartSubType } from '../../models/maintenancelogs.model';
import {
  ActionButtonColumnDefinition,
  ColumnDefinition,
  DataColumnDefinition,
  PaginatorConfig,
} from '../qs-table/column-definition.interface';

interface FlightPartId {
  partType?: string;
  partSubType?: string;
  id?: string;
}

interface FlightPartEntry {
  partId?: FlightPartId;
}

interface FlightConfiguration {
  configuration?: string;
  body?: FlightPartEntry;
  leftWing?: FlightPartEntry;
  rightWing?: FlightPartEntry;
  rear?: FlightPartEntry;
}

interface FlightAutopilot {
  qosVersion?: string;
  serialNumber?: string;
}

interface FlightHistoryRow {
  id: string;
  logId?: string;
  takeOffTime: string;
  flightTimeSeconds?: number;
  configuration?: FlightConfiguration;
  autopilot?: FlightAutopilot;
}

@Component({
  selector: 'app-drone-flights-history',
  templateUrl: './drone-flights-history.component.html',
  styleUrl: './drone-flights-history.component.scss',
  standalone: false,
})
export class DroneFlightsHistoryComponent implements AfterViewInit, OnChanges {
  @Input() device?: DeviceDto;
  @Input() profile: PrivilegedProfile | null = null;
  @Input() configurationType!: string;

  data: FlightHistoryRow[] = [];
  private allRows: FlightHistoryRow[] = [];
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdk = inject(ChangeDetectorRef);
  private readonly injector = inject(Injector);
  private viewInitialized = false;

  @ViewChild('bodyTemplate') bodyTemplate!: TemplateRef<unknown>;
  @ViewChild('leftWingTemplate') leftWingTemplate!: TemplateRef<unknown>;
  @ViewChild('rightWingTemplate') rightWingTemplate!: TemplateRef<unknown>;
  @ViewChild('rearTemplate') rearTemplate!: TemplateRef<unknown>;

  columns: ColumnDefinition<FlightHistoryRow>[] = [];
  paginatorConfig: PaginatorConfig = {
    pageSize: 15,
    pageSizeOptions: [5, 10, 15, 25],
  };

  constructor(
    private readonly deviceService: DeviceService,
    private readonly translate: TranslateService,
  ) {}

  get deviceId(): string | undefined {
    return this.device?.deviceId?.trim() || undefined;
  }

  get deviceName(): string {
    return getDeviceDisplayName(this.device);
  }

  get deviceUnitNumber(): string {
    return getDeviceUnitNumber(this.device);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['device']) {
      this.loadFlightsHistory();
      if (this.viewInitialized) {
        this.createTable();
      }
      return;
    }

    if (changes['configurationType']) {
      this.applyConfigurationFilter();
    }

    if (changes['profile'] && this.viewInitialized) {
      this.createTable();
    }
  }

  ngAfterViewInit() {
    this.viewInitialized = true;
    this.createTable();
  }

  createTable() {
    const logUiColumn: ActionButtonColumnDefinition<FlightHistoryRow> | null =
      this.canShowLogUiColumn()
        ? {
            type: 'action',
            actionType: 'button',
            header: this.translate.instant('drone.flight_his.logui'),
            buttonName: this.translate.instant(
              'drone.flight_his.open_in_logui',
            ),
            iconName: 'open_in_new',
            testId: 'open-in-logui',
            isDisabled: (row) => !this.getLogUiFlightHref(row),
            buttonCallback: (row) => this.openInLogUi(row),
          }
        : null;

    this.columns = [
      {
        type: 'data',
        field: 'id',
        header: this.translate.instant('drone.flight_his.flight_id'),
        sortFn: (a: FlightHistoryRow, b: FlightHistoryRow, multiplier) =>
          a.id.localeCompare(b.id, undefined, { numeric: true }) * multiplier,
      } as DataColumnDefinition<FlightHistoryRow>,
      {
        type: 'data',
        field: 'takeOffTime',
        header: this.translate.instant('drone.flight_his.takeOffTime'),
        format: (row: FlightHistoryRow) => formatTimestamp(row.takeOffTime),
        sortFn: (a: FlightHistoryRow, b: FlightHistoryRow, multiplier) =>
          (new Date(a.takeOffTime).getTime() -
            new Date(b.takeOffTime).getTime()) *
          multiplier,
      } as DataColumnDefinition<FlightHistoryRow>,
      {
        type: 'data',
        field: 'flightTimeSeconds',
        header: this.translate.instant('drone.flight_his.duration'),
        format: (row: FlightHistoryRow) =>
          this.formatFlightDuration(row.flightTimeSeconds),
        sortFn: (a: FlightHistoryRow, b: FlightHistoryRow, multiplier) =>
          ((a.flightTimeSeconds || 0) - (b.flightTimeSeconds || 0)) *
          multiplier,
      } as DataColumnDefinition<FlightHistoryRow>,
      {
        type: 'data',
        field: 'body',
        header: this.translate.instant('drone.flight_his.body'),
        customTemplate: this.bodyTemplate,

        sortFn: (
          a: FlightHistoryRow,
          b: FlightHistoryRow,
          multiplier: number,
        ) => {
          const aKey = a.configuration?.body?.partId?.id ?? '';
          const bKey = b.configuration?.body?.partId?.id ?? '';
          return aKey.localeCompare(bKey) * multiplier;
        },
      } as DataColumnDefinition<FlightHistoryRow>,
      {
        type: 'data',
        field: 'left_wing',
        header: this.translate.instant('drone.flight_his.left_wing'),
        customTemplate: this.leftWingTemplate,
        sortFn: (
          a: FlightHistoryRow,
          b: FlightHistoryRow,
          multiplier: number,
        ) => {
          const aKey = a.configuration?.leftWing?.partId?.id ?? '';
          const bKey = b.configuration?.leftWing?.partId?.id ?? '';
          return aKey.localeCompare(bKey) * multiplier;
        },
      } as DataColumnDefinition<FlightHistoryRow>,
      {
        type: 'data',
        field: 'right_wing',
        header: this.translate.instant('drone.flight_his.right_wing'),
        customTemplate: this.rightWingTemplate,
        sortFn: (
          a: FlightHistoryRow,
          b: FlightHistoryRow,
          multiplier: number,
        ) => {
          const aKey = a.configuration?.rightWing?.partId?.id ?? '';
          const bKey = b.configuration?.rightWing?.partId?.id ?? '';
          return aKey.localeCompare(bKey) * multiplier;
        },
      } as DataColumnDefinition<FlightHistoryRow>,
      {
        type: 'data',
        field: 'rear',
        header: this.translate.instant('drone.flight_his.rear'),
        customTemplate: this.rearTemplate,
        sortFn: (
          a: FlightHistoryRow,
          b: FlightHistoryRow,
          multiplier: number,
        ) => {
          const aKey = a.configuration?.rear?.partId?.id ?? '';
          const bKey = b.configuration?.rear?.partId?.id ?? '';
          return aKey.localeCompare(bKey) * multiplier;
        },
      } as DataColumnDefinition<FlightHistoryRow>,
      {
        type: 'data',
        field: 'qosVersion',
        header: this.translate.instant('drone.flight_his.qos_version'),
        format: (row: FlightHistoryRow) => row.autopilot?.qosVersion,
        sortFn: (
          a: FlightHistoryRow,
          b: FlightHistoryRow,
          multiplier: number,
        ) => {
          const aKey = a.autopilot?.qosVersion ?? '';
          const bKey = b.autopilot?.qosVersion ?? '';
          return aKey.localeCompare(bKey) * multiplier;
        },
      } as DataColumnDefinition<FlightHistoryRow>,
      ...(logUiColumn ? [logUiColumn] : []),
    ];
    this.cdk.detectChanges();
  }

  readonly FLIGHTS_CSV_COLUMNS: CsvColumn<FlightHistoryRow>[] = [
    {
      header: this.translate.instant('drone.flight_his.flight_id'),
      accessor: 'id',
      format: (v) => String(v ?? ''),
    },
    {
      header: this.translate.instant('drone.flight_his.drone_name'),
      accessor: () => this.deviceName,
    },
    {
      header: this.translate.instant('drone.flight_his.drone_unit_no'),
      accessor: () => this.deviceUnitNumber,
    },

    {
      header: this.translate.instant('drone.flight_his.takeOffTime'),
      accessor: 'takeOffTime',
      format: (v) => formatTimestamp(v as string),
    },
    {
      header: this.translate.instant('drone.flight_his.duration'),
      accessor: 'flightTimeSeconds',
      format: (v) =>
        this.formatFlightDuration(
          v !== null && v !== undefined ? Number(v) : undefined,
        ),
    },

    {
      header: this.translate.instant('drone.flight_his.body'),
      accessor: (row) => row.configuration?.body,
      format: (_v, row) => this.formatPartCell(row.configuration?.body),
    },
    {
      header: this.translate.instant('drone.flight_his.left_wing'),
      accessor: (row) => row.configuration?.leftWing,
      format: (_v, row) => this.formatPartCell(row.configuration?.leftWing),
    },
    {
      header: this.translate.instant('drone.flight_his.right_wing'),
      accessor: (row) => row.configuration?.rightWing,
      format: (_v, row) => this.formatPartCell(row.configuration?.rightWing),
    },
    {
      header: this.translate.instant('drone.flight_his.rear'),
      accessor: (row) => row.configuration?.rear,
      format: (_v, row) => this.formatPartCell(row.configuration?.rear),
    },

    {
      header: this.translate.instant('drone.flight_his.qos_version'),
      accessor: 'autopilot.qosVersion',
      format: (v) => String(v ?? ''),
    },
    {
      header: this.translate.instant('drone.flight_his.autopilot_serial'),
      accessor: 'autopilot.serialNumber',
      format: (v) => String(v ?? ''),
    },
  ];

  private formatPartCell(part?: FlightPartEntry): string {
    if (!part?.partId) return '';

    const subtype = part.partId.partSubType ?? '';
    const id = part.partId.id ?? '';

    const partIdLabel = this.translate.instant('drone.flight_his.part_id');
    const formattedSubtype = this.formatPartSubtype(subtype);
    const prefix = formattedSubtype ? `${formattedSubtype} ` : '';
    return `${prefix}(${partIdLabel}: ${id})`;
  }

  formatPartSubtype(partSubType?: string): string {
    return formatPartSubType(partSubType);
  }

  private formatFlightDuration(flightTimeSeconds?: number | null): string {
    if (flightTimeSeconds === undefined || flightTimeSeconds === null) {
      return '';
    }

    const hours = Math.floor(flightTimeSeconds / 3600);
    const minutes = Math.floor((flightTimeSeconds % 3600) / 60);
    const seconds = flightTimeSeconds % 60;
    let formattedTimeString = '';

    if (hours > 0) {
      formattedTimeString += `${hours}h `;
    }
    if (minutes > 0 || hours > 0) {
      formattedTimeString += `${minutes}m `;
    }
    formattedTimeString += `${seconds}s`;
    return formattedTimeString.trim();
  }

  csvExportFlights(): void {
    const csvService = this.injector.get(CsvExportService);
    csvService.exportToCsv(this.data, this.FLIGHTS_CSV_COLUMNS, {
      fileName: `${this.deviceUnitNumber}_flight_history_${this.configurationType}_${buildTimestamp()}.csv`,
    });
  }

  private loadFlightsHistory() {
    if (!this.deviceId) {
      this.data = [];
      return;
    }

    this.deviceService
      .getDeviceSessions(this.deviceId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const sessions = response.sessions ?? [];
          this.allRows = this.mapSessionRowsToFlights(sessions);
          this.applyConfigurationFilter();
        },
        error: (error) => {
          console.error('Error loading flight details from device:', error);
          this.allRows = [];
          this.data = [];
        },
      });
  }

  private applyConfigurationFilter(): void {
    this.data = this.filterByConfiguration(this.allRows);
  }

  private mapSessionRowsToFlights(sessions: SessionDto[]): FlightHistoryRow[] {
    return sessions.map((session, index) => {
      const parts = this.mapSessionParts(session.parts ?? []);
      const details = this.extractUavDetails(session.device?.details);

      return {
        id: session.sessionId ?? `${index + 1}`,
        logId: session.sessionId,
        takeOffTime: session.startTime ?? '',
        flightTimeSeconds: session.durationInSeconds,
        configuration: {
          configuration: resolveSessionConfiguration(session),
          ...parts,
        },
        autopilot: {
          serialNumber: details?.autopilot?.serialNumber,
          qosVersion: details?.software?.qosVersion,
        },
      };
    });
  }

  private mapSessionParts(parts: PartDto[]): {
    body?: FlightPartEntry;
    leftWing?: FlightPartEntry;
    rightWing?: FlightPartEntry;
    rear?: FlightPartEntry;
  } {
    const mapped: {
      body?: FlightPartEntry;
      leftWing?: FlightPartEntry;
      rightWing?: FlightPartEntry;
      rear?: FlightPartEntry;
    } = {};

    parts.forEach((part) => {
      const partEntry = this.mapDevicePart(part);
      switch (part.type) {
        case 'body':
          mapped.body = partEntry;
          break;
        case 'left_wing':
          mapped.leftWing = partEntry;
          break;
        case 'right_wing':
          mapped.rightWing = partEntry;
          break;
        case 'rear':
          mapped.rear = partEntry;
          break;
        default:
          break;
      }
    });

    return mapped;
  }

  private mapDevicePart(part: PartDto): FlightPartEntry {
    const partDetails = part.details;
    const isUavPart = partDetails?.type === 'QsUavPart';

    return {
      partId: {
        partType:
          part.type ??
          (isUavPart ? partDetails.partType?.toLowerCase() : undefined),
        partSubType: isUavPart ? partDetails.partSubType : undefined,
        id: isUavPart ? (partDetails.partId ?? part.partId) : part.partId,
      },
    };
  }

  private filterByConfiguration(rows: FlightHistoryRow[]): FlightHistoryRow[] {
    if (!this.configurationType || this.configurationType === PartSubType.All) {
      return rows;
    }

    const hasResolvedConfiguration = rows.some(
      (row) => !!row.configuration?.configuration,
    );
    if (!hasResolvedConfiguration) {
      return rows;
    }

    return rows.filter((row) => {
      const configuration = row.configuration?.configuration ?? '';
      if (!configuration) {
        return false;
      }

      return matchesDroneConfiguration(configuration, [this.configurationType]);
    });
  }

  private extractUavDetails(
    details?: DeviceDetailsDto,
  ): QsUavDetailsDto | undefined {
    return this.isQsUavDetails(details) ? details : undefined;
  }

  private getLogUiFlightHref(row: FlightHistoryRow): string | undefined {
    const baseUrl = environment.logUiUrl.trim();
    const details = this.device?.details;
    if (
      !baseUrl ||
      !row.logId ||
      !/^\d+$/.test(row.logId) ||
      !this.isAircraftDeviceDetails(details)
    ) {
      return undefined;
    }

    const hardwareRevision = details.aircraftModel?.hardwareRevision;
    const unitId =
      details.unitId ?? Number(this.device?.deviceSummary?.unitId?.trim());
    const logId = Number(row.logId);
    if (
      hardwareRevision === undefined ||
      !Number.isSafeInteger(hardwareRevision) ||
      hardwareRevision < 0 ||
      !Number.isSafeInteger(unitId) ||
      unitId < 0 ||
      !Number.isSafeInteger(logId)
    ) {
      return undefined;
    }

    const logUiHardwareRevision =
      resolveLogUiHardwareRevision(hardwareRevision);

    try {
      const url = new URL('/logui', baseUrl);
      url.searchParams.set(
        'vehicleId',
        `ch-${logUiHardwareRevision}-${unitId}`,
      );
      url.searchParams.set('logId', String(logId));
      url.searchParams.set('tab', 'analysis');
      return url.toString();
    } catch {
      return undefined;
    }
  }

  private canShowLogUiColumn(): boolean {
    // Only permitted users with supported aircraft receive a LogUI action.
    return (
      allowOpenLogUILinks(this.profile) &&
      !!environment.logUiUrl.trim() &&
      this.isAircraftDeviceDetails(this.device?.details)
    );
  }

  private openInLogUi(row: FlightHistoryRow): void {
    const href = this.getLogUiFlightHref(row);
    if (href) {
      window.open(href, '_blank', 'noopener,noreferrer');
    }
  }

  private isAircraftDeviceDetails(
    details?: DeviceDetailsDto,
  ): details is QsUavDetailsDto | QsTrinityProDetailsDto {
    return details?.type === 'QsUav' || details?.type === 'QsTrinityPro';
  }

  private isQsUavDetails(
    details?: DeviceDetailsDto,
  ): details is QsUavDetailsDto {
    return details?.type === 'QsUav';
  }
}
